import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import DocumentEditor from "@/components/DocumentEditor";
import StatusBadge from "@/components/StatusBadge";
import UploadPdfDialog from "@/components/UploadPdfDialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Inbox, FileText, RefreshCw, CalendarClock, CalendarDays } from "lucide-react";
import { MobileSidebar, BurgerBar } from "@/components/MobileNav";

const STATUS_OPTIONS = [
  { k: "alle", label: "Alle Status" },
  { k: "neu", label: "Neu" },
  { k: "fertig", label: "Fertig" },
  { k: "zurueckgestellt", label: "Zurückgestellt" },
];
const SOURCE_OPTIONS = [
  { k: "alle", label: "Alle Herkünfte" },
  { k: "crawl", label: "Crawler" },
  { k: "upload", label: "Upload" },
];

const isToday = (iso) => iso && dayjs(iso).isSame(dayjs(), "day");
const matchStatus = (d, s) => (s === "alle" ? true : d.status === s);
const matchSource = (d, s) => (s === "alle" ? true : (d.source || "crawl") === s);

// Bundesland = Zeichen vor dem 1. "_", Heft-Art = Zeichen vor dem 2. "_" im Dateinamen.
const parseName = (fn = "") => {
  const parts = (fn || "").split("_");
  return { land: parts[0] || "", blatt: parts[1] || "" };
};
const landOf = (d) => d.land || parseName(d.filename).land;
const blattOf = (d) => d.blatt || parseName(d.filename).blatt;

export default function HefteHeute() {
  const location = useLocation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("neu");
  const [sourceFilter, setSourceFilter] = useState("alle");
  const [todayOnly, setTodayOnly] = useState(true);
  const [landFilter, setLandFilter] = useState("alle");
  const [blattFilter, setBlattFilter] = useState("alle");
  const [selectedId, setSelectedId] = useState(null);
  const [jump, setJump] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const selectedRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/documents"); setDocs(r.data); }
    catch { toast.error("Dokumente konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUploaded = useCallback((doc) => {
    setStatusFilter("alle");
    setSourceFilter("alle");
    setTodayOnly(false);
    setSelectedId(doc?.id || null);
    setJump(null);
    load();
  }, [load]);

  // Aus globaler Suche oder Dashboard geöffnetes Dokument (mit Sprung zur Seite).
  useEffect(() => {
    const st = location.state;
    if (st?.openDoc) {
      setStatusFilter("alle");
      setSourceFilter("alle");
      setTodayOnly(false);
      setSelectedId(st.openDoc);
      setJump({ page: st.jumpPage || 0, nonce: st.ts || Date.now() });
    }
  }, [location.state]);

  const filtered = useMemo(() => {
    return docs.filter((d) =>
      matchStatus(d, statusFilter)
      && matchSource(d, sourceFilter)
      && (!todayOnly || isToday(d.created_at))
      && (landFilter === "alle" || landOf(d) === landFilter)
      && (blattFilter === "alle" || blattOf(d) === blattFilter));
  }, [docs, statusFilter, sourceFilter, todayOnly, landFilter, blattFilter]);

  // Filter-Optionen aus den vorhandenen Dokumenten ableiten.
  const landOptions = useMemo(() => {
    const s = new Set(docs.map(landOf).filter(Boolean));
    return Array.from(s).sort();
  }, [docs]);
  const blattOptions = useMemo(() => {
    const s = new Set(
      docs.filter((d) => landFilter === "alle" || landOf(d) === landFilter).map(blattOf).filter(Boolean));
    return Array.from(s).sort();
  }, [docs, landFilter]);

  // Heft-Art zurücksetzen, wenn sie im aktuellen Bundesland nicht mehr existiert.
  useEffect(() => {
    if (blattFilter !== "alle" && !blattOptions.includes(blattFilter)) setBlattFilter("alle");
  }, [blattOptions, blattFilter]);

  const counts = useMemo(() => {
    const scope = docs.filter((d) =>
      matchSource(d, sourceFilter)
      && (!todayOnly || isToday(d.created_at))
      && (landFilter === "alle" || landOf(d) === landFilter)
      && (blattFilter === "alle" || blattOf(d) === blattFilter));
    return {
      alle: scope.length,
      neu: scope.filter((d) => d.status === "neu").length,
      fertig: scope.filter((d) => d.status === "fertig").length,
      zurueckgestellt: scope.filter((d) => d.status === "zurueckgestellt").length,
    };
  }, [docs, sourceFilter, todayOnly, landFilter, blattFilter]);

  // Ausgewähltes Heft in den sichtbaren Bereich der Liste scrollen (z.B. nach Sprung aus Dashboard).
  useEffect(() => {
    if (selectedId && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedId, filtered]);

  const selectedDoc = docs.find((d) => d.id === selectedId);

  const sidebar = (
    <>
      <div className="shrink-0 p-3 border-b border-gray-200 bg-white space-y-2.5">
        <div className="[&>button]:w-full">
          <UploadPdfDialog onUploaded={onUploaded} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => setTodayOnly((v) => !v)} data-testid="hefte-today-toggle" title="Zwischen 'Nur heute' und 'Alle Tage' umschalten"
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${todayOnly ? "bg-[#0F4C81]/10 text-[#0F4C81] border-[#0F4C81]/30" : "bg-white text-gray-500 border-gray-300 hover:bg-gray-50"}`}>
            {todayOnly ? <CalendarClock className="h-3.5 w-3.5" /> : <CalendarDays className="h-3.5 w-3.5" />}
            {todayOnly ? "Nur heute" : "Alle Tage"}
          </button>
          <Button size="sm" variant="outline" onClick={load} data-testid="hefte-refresh" title="Liste aktualisieren"><RefreshCw className="h-4 w-4" /></Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid="hefte-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.k} value={o.k} data-testid={`hefte-filter-status-${o.k}`}>{o.label} ({counts[o.k]})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid="hefte-filter-source"><SelectValue placeholder="Herkunft" /></SelectTrigger>
            <SelectContent>
              {SOURCE_OPTIONS.map((o) => (
                <SelectItem key={o.k} value={o.k} data-testid={`hefte-filter-source-${o.k}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Select value={landFilter} onValueChange={(v) => { setLandFilter(v); setBlattFilter("alle"); }}>
            <SelectTrigger className="h-8 text-xs" data-testid="hefte-filter-land"><SelectValue placeholder="Bundesland" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle" data-testid="hefte-filter-land-alle">Alle Bundesländer</SelectItem>
              {landOptions.map((l) => (
                <SelectItem key={l} value={l} data-testid={`hefte-filter-land-${l}`}>{l.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={blattFilter} onValueChange={setBlattFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid="hefte-filter-blatt"><SelectValue placeholder="Heft-Art" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle" data-testid="hefte-filter-blatt-alle">Alle Heft-Arten</SelectItem>
              {blattOptions.map((b) => (
                <SelectItem key={b} value={b} data-testid={`hefte-filter-blatt-${b}`}>{b.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-16 flex justify-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Inbox className="h-7 w-7 mx-auto" />
            <p className="mt-2 text-sm">Keine Hefte in dieser Ansicht.</p>
            {todayOnly && <p className="text-xs">Tipp: „Alle Tage" einschalten.</p>}
          </div>
        ) : (
          filtered.map((d) => {
            const active = d.id === selectedId;
            return (
              <button key={d.id} ref={active ? selectedRef : null} onClick={() => { setSelectedId(d.id); setJump(null); setNavOpen(false); }} data-testid={`hefte-doc-${d.id}`}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${active ? "bg-[#0F4C81]/10 border-l-4 border-l-[#0F4C81] ring-1 ring-inset ring-[#0F4C81]/30" : "border-l-4 border-l-transparent hover:bg-white"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-mono text-base truncate ${active ? "text-[#0F4C81] font-semibold" : "text-gray-800"}`}>{d.filename}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${d.source === "upload" ? "bg-gray-100 text-gray-600 border-gray-300" : "bg-sky-50 text-sky-800 border-sky-300"}`}>{d.source === "upload" ? "Upload" : "Crawl"}</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-gray-400">{d.created_at ? dayjs(d.created_at).format("DD.MM.YY HH:mm") : "—"}</span>
                  <StatusBadge status={d.status} testId={`hefte-doc-status-${d.id}`} />
                </div>
              </button>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <div className="h-full flex">
      {/* list — desktop inline */}
      <div className="hidden xl:flex w-96 shrink-0 border-r border-gray-200 bg-gray-50 flex-col min-h-0" data-testid="hefte-list">
        {sidebar}
      </div>
      {/* list — mobile burger sheet */}
      <MobileSidebar open={navOpen} onOpenChange={setNavOpen} title="Hefte">
        <div className="flex flex-col min-h-0 h-full bg-gray-50">{sidebar}</div>
      </MobileSidebar>

      {/* editor */}
      <div className="flex-1 min-w-0 h-full bg-white flex flex-col min-h-0">
        <BurgerBar onOpen={() => setNavOpen(true)} label={selectedDoc ? selectedDoc.filename : "Heft wählen"} />
        <div className="flex-1 min-h-0">
          {selectedId ? (
            <DocumentEditor docId={selectedId} jump={jump} onSaved={load} />
          ) : (
            <div className="h-full flex items-center justify-center text-center p-8">
              <div className="text-gray-300">
                <FileText className="h-12 w-12 mx-auto" />
                <p className="mt-3 text-sm text-gray-500">Wählen Sie ein Heft, um den Viewer & Editor zu öffnen.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
